<template>
  <div class="nav_wrapper">
    <div class="sidebar_background"></div>
    <div class="top_bar">
      <router-link :to="{ name: RouteName.Profile }" class="profile_link" id="nav-profile" v-focus data-down="nav-home" data-right="nav-language">
        <IconAccount class="profile_icon" />
      </router-link>
      <img class="player_icon" src="@/assets/images/icons/playerIconPink.svg" alt="TG4PlayerIcon" />
      <div class="spacer"></div>
      <TG4Button
        id="nav-language"
        class="language_button"
        v-focus
        data-left="nav-profile"
        data-right="nav-settings"
        v-press="toggleLanguage"
        @down="moveFocusToNestedView"
        :title="languageToChangeToTitle"
      />
      <router-link :to="{ name: RouteName.Settings }" class="settings_link" id="nav-settings" v-focus data-left="nav-language" @down="moveFocusToNestedView">
        <IconSettings class="settings_icon" />
      </router-link>
    </div>
    <div class="main_area">
      <div class="sidebar">
        <router-link class="nav_link" v-for="(item, index) in navLinks"
            :id="item.id"
            :key="item.routeName" 
            :to="{ name: item.routeName }" 
            v-focus 
            v-focus-on-insert="index === 0"
            :data-default="index === 0"
            :data-up="index === 0 ? 'nav-profile' : 'AUTOFOCUS'"
            data-down="AUTOFOCUS"
            @right="moveFocusToNestedView">
          <component :is="item.icon" class="nav_link_icon" />
          <p class="nav_link_text">
            {{ $t(item.translationKey) }}
          </p>
        </router-link>
      </div>
      <div class="content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import { RouterLink } from 'vue-router';
import { RouteName } from '@/router/routes';
import { Language } from '@/enums/language';
import TG4Button from "@/components/TG4Button.vue";
import { useStore } from "@/composables/useStore";
import { IconHome, IconLive, IconSearch, IconAccount, IconSettings, IconAdd } from '@/components/icons';
import { FocusElement, navigationService } from "vue-spatialnavigation";
import { railFocusMemory } from "@/helpers/railFocusMemory";
const { useGetter, dispatch } = useStore();
const currentLanguage = useGetter<Language>('currentLanguage');

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;

const i18n = instance?.proxy?.$i18n;

//Fix for typing Vue components correctly
type IconComponent = typeof IconHome;

interface NavLink {
  id: string;
  routeName: RouteName;
  icon: IconComponent;
  translationKey: string;
}

const navLinks: NavLink[] = [
  {
    id: 'nav-home',
    routeName: RouteName.Home,
    icon: IconHome,
    translationKey: 'general.home'
  },
  {
    id: 'nav-search',
    routeName: RouteName.Search,
    icon: IconSearch,
    translationKey: 'general.search'
  },
  {
    id: 'nav-discover',
    routeName: RouteName.Discover,
    icon: IconAdd,
    translationKey: 'general.discover'
  },
  {
    id: 'nav-live',
    routeName: RouteName.Live,
    icon: IconLive,
    translationKey: 'general.live'
  }
];

const languageToChangeToTitle = computed(() => {
  return currentLanguage.value === Language.English ? 'Gaeilge' : 'English';
});

const toggleLanguage = () => {
  const currentLang = currentLanguage.value;
  const newLang = currentLang === Language.English ? Language.Irish : Language.English;
  dispatch("CHANGE_LANGUAGE", newLang);
  if (i18n) {
    i18n.locale = currentLanguage.value.toUpperCase();
  }
};

const moveFocusToNestedView = () => {
  let elementToFocus: FocusElement | undefined;

  switch (router?.currentRoute.name) {
    case RouteName.Rails: {
      elementToFocus =
        navigationService.getFocusElementById(`rail-${railFocusMemory.get()}-0`)
        ?? navigationService.getFocusElementById("rail-0-0");

      break;
    }
    case RouteName.Discover: {
      elementToFocus = navigationService.getFocusElementById('discover-category-0')
        ?? navigationService.getFocusElementById('rail-0-0');
      break;
    }
    case RouteName.Search: {
      elementToFocus = navigationService.getFocusElementById('search-input');
      break;
    }
    case RouteName.Live: {
      elementToFocus = navigationService.getFocusElementById('stream-0');
      if (!elementToFocus) {
        elementToFocus = navigationService.getFocusElementById('epg-button');
      }
      break;
    }
    case RouteName.Welcome:
    case RouteName.Settings: {
      // const elementByClassName = document.getElementsByClassName('firstFocus')[0];
      // elementToFocus = navigationService.getFocusElementById(elementByClassName.id);
      elementToFocus = navigationService.getFocusElementById('faq-button');
      break;
    }
    case RouteName.Login: {
      elementToFocus = navigationService.getFocusElementById('login-button');
      break;
    }
    case RouteName.UserBookmarks: {
      elementToFocus = navigationService.getFocusElementById('nav-user-bookmarks');
      break;
    }
    case RouteName.UserHistory: {
      elementToFocus = navigationService.getFocusElementById('nav-user-history');
      break;
    }
    case RouteName.UserDetails: {
      elementToFocus = navigationService.getFocusElementById('nav-user-details');
      break;
    }
    default:
      break;
  }

  if (elementToFocus) {
    elementToFocus.focus();
    setTimeout(() => {
      // eslint-disable-next-line
      // @ts-ignore
      document.getElementById(elementToFocus?.id)?.focus();
    }, 200);
  }
};

</script>

<style lang="scss" scoped>

.sidebar_background {
  position: fixed;
  top: 0;
  left: 0;
  width: 160px;
  height: 100%;
  background-color: $color-sidebar;
  z-index: -1;
}

.main_area {
  display: flex;
  flex: 1;
  background-color: $color-background-grey;
  //Stop main expanding and force child components to do the scrolling
  min-height: 0;
}

.top_bar {
  height: 150px;
  display: flex;
  align-items: center;
  color: $color-text-white;
  padding: 0px 60px;
  flex-shrink: 0;
}

.spacer {
  flex: 1;
}

.profile_link {
  display: flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
  &:focus {
    color: $color-pink;
  }
}

.profile_icon {
  width: 50px;
  height: 50px;
}

.settings_link {
  display: flex;
  align-items: center;
  margin-right: 0px;
  color: inherit;
  text-decoration: none;
  &:focus {
    color: $color-pink;
  }
}

.language_button {
  margin-right: 60px;
}

.settings_icon {
  width: 50px;
  height: 50px;
}

.player_icon {
  height: 52px;
  width: auto;
  margin-left: 70px;
}

.sidebar {
  width: 160px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  align-items: center;
  background-color: $color-sidebar;
}


.content {
  flex: 1;
  display: flex;
  //Override auto on default flex divs so we don't auto expand
  min-width: 0;

  // The only scrolling region — the top bar and sidebar stay put.
  overflow-x: hidden;
  overflow-y: auto;

  // Hidden scrollbar, matching html/body in App.vue. TVs have no pointer.
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  // Apply to direct child of content too to stop duplication across nested components
  > * {
    flex: 1;
    min-width: 0;
  }
}

.nav_wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  // The page itself never scrolls; .content does.
  overflow: hidden;
}

.nav_link {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 160px;
  text-decoration: none;
  color: $color-off-white;
  width: 100%;
  outline: none;


  &.router-link-active {
    @include text-pink;
  }
  //After active-link so it overrides
  //We use :focus instead of .focus as Vue overrides classes on page change
  //Safe to use :focus as its a anchortag with a a tab index
  &:focus {
    background: $color-pink;
    @include text-white;
  }
}

.nav_link_icon {
  width: 40px;
  height: 40px;
}

.nav_link_text {
  @include text-button;
  margin-top: 20px;
  margin-bottom: 10px;
  text-align: center;
}

</style>
